import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, ModalContext, SectionDivider, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { usePaginationRuleForm } from '@hooks/usePaginationRule'

import EntitySelector from '@components/EntitySelector'

import usePaginationRuleSort from '@hooks/usePaginationRuleSort'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { sortOptions } from '@functions/paginationRule'

import type { ActionResult } from '@redux/modules/types'

import type { UsePaginationRulePayload } from '@hooks/usePaginationRule'

import type { PaginationRuleModel } from '@models/types'

import SortListItem from './SortListItem'

interface ModalCallbacks {
  closeModal: () => void,
  createPaginationRule?: (payload: HandleCallbackActionParams) => Promise<ActionResult<PaginationRuleModel>>,
  updatePaginationRule?: (payload: HandleCallbackActionParams) => Promise<ActionResult<PaginationRuleModel>>,
}

type CreateOrUpdatePaginationRuleParams = {
  entityState: Partial<PaginationRuleModel>,
  callbacks: ModalCallbacks,
  createFn: UsePaginationRulePayload['callbacks']['createPaginationRule'],
  updateFn: UsePaginationRulePayload['callbacks']['updatePaginationRule'],
}

const createOrUpdatePaginationRule = (params: CreateOrUpdatePaginationRuleParams) => {
  const {
    entityState, callbacks, createFn, updateFn,
  } = params

  const { closeModal, createPaginationRule, updatePaginationRule } = callbacks || {}

  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const paginationRulePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: !entityState.id,
    toastText: `Pagination Rule ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id && updatePaginationRule) return updatePaginationRule(paginationRulePayload)
  // @ts-expect-error TS2345
  if (createPaginationRule) return createPaginationRule(paginationRulePayload)

  return Promise.resolve({})
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditPaginationRuleModal = (props: ModalProps) => {
  const {
    callbacks, modalKey = 'CreateOrEditPaginationRuleModal', showModal,
  } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { paginationRule } = modalPayload

  const paginationRuleFormPayload = usePaginationRuleForm(paginationRule)

  const {
    entityState,
    entityState: {
      brand_id,
      default: entityDefault,
      group_items_by,
      id,
      placement_direction,
      start_page,
      sort_items_by,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = paginationRuleFormPayload

  const {
    callbacks: {
      createPaginationRule: createFn,
      updatePaginationRule: updateFn,
      setParams,
      setSortOrderState,
      toggleSortItem,
    },
    creating,
    disabled,
    descEnabled,
    sortItemsType,
    sortOrder,
    updating,
  } = usePaginationRuleSort({ paginationRule, paginationRuleFormPayload })

  const hasSortItems = !!sort_items_by.length

  const saveOrCreateButtonText = id ? 'Update Pagination Rule' : 'Create Pagination Rule'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Create'}
        titleSecondLine={id ? 'Sort Rules' : 'Pagination Rule'}
      />

      <SidebarModal.Content>
        <Box flexDirection="column">
          {!id && (
            <>
              <EntitySelector
                callbacks={{
                  selectItem: brand => setEntityState({ brand_id: brand ? brand.id : null }),
                }}
                entityKey="brands"
                errorMessage={errors.brand_id}
                label="* Brand"
                selectedItemId={brand_id}
              />

              <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
                <input type="text" name="title" value={title} {...handlers} />
              </Form.Field>

              <Form.Field label="* Default" marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select
                  data-value-type="boolean"
                  name="default"
                  value={entityDefault}
                  {...handlers}
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </Form.Field>

              <Form.Field label="Group Items By" marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select name="group_items_by" value={group_items_by} {...handlers}>
                  <option value="">Default</option>
                  <option value="campaign.state_name">Campaign State Name</option>
                  <option value="order_item.group_key">Order Item Group Key</option>
                </select>
              </Form.Field>

              <Form.Field label="Placement Direction" marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select
                  name="placement_direction"
                  value={placement_direction}
                  {...handlers}
                >
                  <option value="">Default</option>
                  <option value="RTL">Right to Left</option>
                  <option value="LTR">Left to Right</option>
                </select>
              </Form.Field>

              <Form.Field label="Start Page" marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select
                  name="start_page"
                  value={start_page}
                  {...handlers}
                >
                  <option value="">Default</option>
                  <option value="LHS">LHS</option>
                  <option value="RHS">RHS</option>
                </select>
              </Form.Field>

              <SectionDivider boxProps={{ marginY: 'large' }}>SORT ITEMS BY</SectionDivider>
            </>
          )}

          <Form.Field errorMessage={errors.sort_items_by}>
            <select value={sortItemsType} onChange={e => setParams!(e.target.value)}>
              {sortOptions.map(sortOption => (
                <option key={sortOption.key} value={sortOption.key}>{sortOption.label}</option>
              ))}
            </select>
          </Form.Field>

          <Form.Field marginTop="large">
            <select value={sortOrder} onChange={e => setSortOrderState(e.target.value)}>
              {descEnabled && <option value="desc">Descending (Biggest to Smallest)</option>}
              {sortItemsType !== 'promo' && <option value="asc">Ascending (Smallest to Biggest)</option>}
            </select>
          </Form.Field>

          <Button
            buttonStyle="secondaryEdit"
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faPlus} />}
            // @ts-expect-error
            marginTop="large"
            onClick={() => toggleSortItem(`${sortItemsType}.${sortOrder}`)}
            size="medium"
          >
            Add To Sort
          </Button>

          {hasSortItems && (
            <>
              <Text
                color="bodyFontLightColor"
                fontSize={12}
                letterSpacing="1px"
                marginTop="large"
              >
                Applied Sorting
              </Text>

              {sort_items_by.map((sortItem: string) => (
                <SortListItem
                  callbacks={{ toggleSortItem }}
                  key={sortItem}
                  sortItem={sortItem}
                />
              ))}
            </>
          )}
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdatePaginationRule({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPaginationRuleModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
